import { DisplayColumnsMenuTrigger } from "@repo/ui/Menu/displayColumnsMenuTrigger";
import { MultiSelectMenuPopover } from "@repo/ui/Menu/multiSelectMenuPopover";
import type { MultiSelectSection } from "@repo/ui/Select/multiSelect";
import { useCallback, useMemo, type FC } from "react";
import { MenuTrigger } from "react-aria-components";

interface DisplayColumnsSelectorProps {
  /**
   * Optional "Performance metrics" sections. When provided, rendered
   * first in the menu (above section / custom-field groups).
   */
  metricColumnSections?: readonly MultiSelectSection[];
  metricColumnSelectedIds?: readonly string[];
  onMetricColumnSelectionChange?: (keys: readonly string[]) => void;
  sectionColumnSections?: readonly MultiSelectSection[];
  sectionColumnSelectedIds?: readonly string[];
  onSectionColumnSelectionChange?: (keys: readonly string[]) => void;
  customFieldSections: readonly MultiSelectSection[];
  customFieldSelectedIds: readonly string[];
  onCustomFieldSelectionChange: (keys: readonly string[]) => void;
}

/**
 * A combined "Display columns" dropdown that merges the optional
 * "Performance metrics" group with playbook section column options and
 * custom field column options into a single multi-select menu.
 */
export const DisplayColumnsSelector: FC<DisplayColumnsSelectorProps> = ({
  metricColumnSections = [],
  metricColumnSelectedIds = [],
  onMetricColumnSelectionChange,
  sectionColumnSections = [],
  sectionColumnSelectedIds = [],
  onSectionColumnSelectionChange,
  customFieldSections,
  customFieldSelectedIds,
  onCustomFieldSelectionChange,
}) => {
  const allSections = useMemo(
    () => [
      ...metricColumnSections,
      ...sectionColumnSections,
      ...customFieldSections,
    ],
    [metricColumnSections, sectionColumnSections, customFieldSections],
  );

  const allSelectedIds = useMemo(
    () => [
      ...metricColumnSelectedIds,
      ...sectionColumnSelectedIds,
      ...customFieldSelectedIds,
    ],
    [metricColumnSelectedIds, sectionColumnSelectedIds, customFieldSelectedIds],
  );

  // Build sets of all IDs belonging to each selector
  const metricOptionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const section of metricColumnSections) {
      for (const option of section.options) {
        ids.add(option.id);
      }
    }
    return ids;
  }, [metricColumnSections]);

  const sectionOptionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const section of sectionColumnSections) {
      for (const option of section.options) {
        ids.add(option.id);
      }
    }
    return ids;
  }, [sectionColumnSections]);

  const customFieldOptionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const section of customFieldSections ?? []) {
      for (const option of section.options) {
        ids.add(option.id);
      }
    }
    return ids;
  }, [customFieldSections]);

  const handleChange = useCallback(
    (keys: readonly string[]) => {
      const metricKeys = keys.filter((k) => metricOptionIds.has(k));
      const sectionKeys = keys.filter((k) => sectionOptionIds.has(k));
      const customFieldKeys = keys.filter((k) => customFieldOptionIds.has(k));
      onMetricColumnSelectionChange?.(metricKeys);
      onSectionColumnSelectionChange?.(sectionKeys);
      onCustomFieldSelectionChange(customFieldKeys);
    },
    [
      metricOptionIds,
      sectionOptionIds,
      customFieldOptionIds,
      onMetricColumnSelectionChange,
      onSectionColumnSelectionChange,
      onCustomFieldSelectionChange,
    ],
  );

  if (
    allSections.length === 0 ||
    allSections.every((s) => s.options.length === 0)
  ) {
    return null;
  }

  return (
    <MenuTrigger>
      <DisplayColumnsMenuTrigger />
      <MultiSelectMenuPopover
        ariaLabel="Display columns"
        placement="bottom end"
        sections={allSections}
        value={allSelectedIds}
        onChange={handleChange}
      />
    </MenuTrigger>
  );
};
